import { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useSEO } from "../hooks/useSEO";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "name", label: "Name: A–Z" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [activeSub, setActiveSub] = useState("all");
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Shop All Furniture — Affordable & High Quality | Nesta",
    description:
      "Browse Nesta's full collection of affordable, high quality furniture — sofas, armchairs, beds, dining tables, coffee tables and outdoor seating. Filter by price and category. Free UK delivery.",
  });

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (active !== "all") params.category = active;
    if (activeSub !== "all") params.subcategory = activeSub;
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [active, activeSub]);

  const selectCategory = (c) => {
    setActive(c);
    setActiveSub("all");
  };

  const clearFilters = () => {
    setSearch("");
    setSort("featured");
    setMinPrice("");
    setMaxPrice("");
  };

  const activeCat = categories.find((c) => c.category === active);
  const subcategories = activeCat ? activeCat.subcategories : [];

  const visible = useMemo(() => {
    let list = [...products];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((p) => `${p.name} ${p.description}`.toLowerCase().includes(q));
    const min = parseFloat(minPrice);
    const max = parseFloat(maxPrice);
    if (!Number.isNaN(min)) list = list.filter((p) => p.price >= min);
    if (!Number.isNaN(max)) list = list.filter((p) => p.price <= max);
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => (b.featured === true) - (a.featured === true));
    return list;
  }, [products, search, sort, minPrice, maxPrice]);

  const filtersActive = search || minPrice || maxPrice || sort !== "featured";

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <div className="mb-12">
        <div className="text-sm uppercase tracking-luxe text-[#999] mb-3">The Collection</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight">Shop All</h1>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-3 mb-6 border-b border-[#e5e5e5] pb-6">
        {["all", ...categories.map((c) => c.category)].map((c) => (
          <button
            key={c}
            data-testid={`filter-${c}`}
            onClick={() => selectCategory(c)}
            className={`px-5 py-2 text-sm uppercase tracking-luxe transition-colors ${
              active === c ? "bg-[#1a1a1a] text-white" : "text-[#666] hover:text-black"
            }`}
          >
            {c === "all" ? "All" : c}
          </button>
        ))}
      </div>

      {/* Sub-category filter */}
      {subcategories.length > 0 && (
        <div data-testid="subcategory-filters" className="flex flex-wrap gap-2 mb-8">
          {["all", ...subcategories].map((s) => (
            <button
              key={s}
              data-testid={`subfilter-${s}`}
              onClick={() => setActiveSub(s)}
              className={`px-4 py-1.5 text-xs uppercase tracking-luxe border transition-colors ${
                activeSub === s
                  ? "border-[#1a1a1a] bg-[#1a1a1a] text-white"
                  : "border-[#ddd] text-[#666] hover:border-[#1a1a1a]"
              }`}
            >
              {s === "all" ? "All " + active : s}
            </button>
          ))}
        </div>
      )}

      {/* Search / price / sort filters */}
      <div data-testid="product-filters" className="flex flex-col lg:flex-row lg:items-end gap-4 mb-10">
        <div className="flex-1">
          <label className="text-[11px] uppercase tracking-luxe text-[#999] mb-2 block">Search</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
            <input
              data-testid="filter-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search furniture…"
              className="w-full border border-[#ddd] pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a]"
            />
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-luxe text-[#999] mb-2 block">Price (£)</label>
          <div className="flex items-center gap-2">
            <input
              data-testid="filter-min-price"
              type="number"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder="Min"
              className="w-24 border border-[#ddd] px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a]"
            />
            <span className="text-[#999]">—</span>
            <input
              data-testid="filter-max-price"
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="Max"
              className="w-24 border border-[#ddd] px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a]"
            />
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-luxe text-[#999] mb-2 block">Sort by</label>
          <select
            data-testid="filter-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="border border-[#ddd] px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] bg-white"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        {filtersActive && (
          <button
            data-testid="clear-filters"
            onClick={clearFilters}
            className="flex items-center gap-2 text-sm text-[#666] hover:text-black py-2.5"
          >
            <SlidersHorizontal size={15} /> Clear
          </button>
        )}
      </div>

      <div className="text-sm text-[#999] mb-6" data-testid="results-count">
        {visible.length} {visible.length === 1 ? "product" : "products"}
      </div>

      {loading ? (
        <div className="py-24 text-center text-[#999] tracking-luxe uppercase text-sm">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="py-24 text-center text-[#999] tracking-luxe uppercase text-sm">No products match your filters</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
          {visible.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
